/**
 * ImportDetailPage — `/admin/imports/:id` per-import detail surface.
 *
 * Per `.design-incoming/admin-20260522/IMPORTS_SPEC.md §5`. Same
 * SCRUM-15 epic / SCRUM-125 ticket as the queue page. Eighth per-
 * surface consumer of SCRUM-117's `<IfAdminPermission>` gate.
 *
 * Phase 1 scope:
 *
 *   - Permission gate via `imports:read`.
 *   - Discriminated `useImportDetail` hook (mirrors the SCRUM-121
 *     / 122 / 123 doctrine): `not_wired | loading | not_found |
 *     found`. Phase-1 default 'not_wired' → `BackendNotWiredPanel`
 *     (distinct from a real `NotFoundPanel`). Tests inject via
 *     `importOverride`.
 *   - Header: breadcrumb back-link + type chip + company link +
 *     status badge + initiator email + relative timestamp.
 *   - **Source file** section: filename + size + "Download
 *     original" button (Phase-1 visible placeholder).
 *   - **Progress** section: progress bar + "N / M rows" counter.
 *   - **Errors** section (only when status is `failed` or
 *     `completed_with_errors`): inline list capped at 50 entries,
 *     each row shows row-number + column + human message + raw
 *     value (mono). "Download error report CSV" button.
 *   - **Actions per status** (spec §5 table):
 *       queued                → Cancel queue (gated on `imports:run`)
 *       running               → (none — let it finish; Phase 1 limit)
 *       completed             → Re-run with updated file
 *       completed_with_errors → Re-run + Download error report + Acknowledge errors
 *       failed                → Re-run + Download error report
 *
 * Phase-1 actions: every action button fires no BE mutation today
 * — the companion endpoints haven't shipped. Action buttons are
 * visible placeholders. The contract is the affordance set + the
 * per-status visibility table, not the network behavior.
 */

import * as React from 'react';
import { Link, useParams } from 'react-router-dom';
import { Button, Card } from '@sparta/ui-kit';
import { IfAdminPermission } from '../../../shared/auth/IfAdminPermission.js';
import { useAdminPermissions } from '../../../shared/auth/use-admin-permissions.js';
import {
  ImportStatusDot,
  type ImportStatus,
} from '../_components/ImportStatusDot.js';
import {
  ImportTypeChip,
  importTypeLabel,
} from '../_components/ImportTypeChip.js';
import { formatRelativeTime } from './StaffListPage.js';
import { ProgressBar, type ImportRow } from './ImportsQueuePage.js';

// ── Types ──────────────────────────────────────────────────────

export interface ImportErrorEntry {
  readonly row_number: number;
  readonly column: string;
  /** Human message — never a raw stack trace per spec §9. */
  readonly message: string;
  /** Raw input that caused the error, displayed in monospace. */
  readonly raw_value: string;
}

export interface ImportDetail extends ImportRow {
  readonly source_file_name: string;
  readonly source_file_size_bytes: number;
  readonly errors: ReadonlyArray<ImportErrorEntry>;
}

export type ImportDetailResult =
  | { readonly status: 'not_wired' }
  | { readonly status: 'loading' }
  | { readonly status: 'not_found' }
  | { readonly status: 'found'; readonly importRecord: ImportDetail };

function useImportDetail(_id: string | undefined): ImportDetailResult {
  return React.useMemo<ImportDetailResult>(
    () => ({ status: 'not_wired' }),
    [],
  );
}

// ── Status label / human file size ─────────────────────────────

const STATUS_LABELS: Record<ImportStatus, string> = {
  queued: 'Queued',
  running: 'Running',
  completed: 'Completed',
  completed_with_errors: 'Completed with errors',
  failed: 'Failed',
};

/**
 * Pure file-size formatter. "2.4 MB", "812 KB", "1 B". Exported
 * so the spec can pin its adversarial input classes (zero,
 * boundary, max, malformed).
 */
export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb < 10 ? kb.toFixed(1) : Math.round(kb)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
  const gb = mb / 1024;
  return `${gb < 10 ? gb.toFixed(1) : Math.round(gb)} GB`;
}

// ── Page entry ─────────────────────────────────────────────────

export interface ImportDetailPageProps {
  /**
   * Test injection — overrides the `useImportDetail` hook.
   *
   *   `undefined` (default): hook runs; Phase-1 returns
   *                           `BackendNotWiredPanel`.
   *   `null`                : simulate the `'not_found'` branch.
   *   `ImportDetail`        : simulate the loaded surface.
   */
  readonly importOverride?: ImportDetail | null;
}

export function ImportDetailPage(
  props: ImportDetailPageProps = {},
): React.ReactElement {
  return (
    <IfAdminPermission permission="imports:read" fallback={<NotPermittedPanel />}>
      <ImportDetailBody {...props} />
    </IfAdminPermission>
  );
}

function NotPermittedPanel(): React.ReactElement {
  return (
    <section
      aria-labelledby="import-not-permitted-heading"
      className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-16 text-center"
    >
      <h1
        id="import-not-permitted-heading"
        className="text-xl font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        Not permitted
      </h1>
      <p className="mt-2 text-sm" style={{ color: 'var(--fg-secondary)' }}>
        You don't have access to view Imports. Ask a platform
        administrator if you need this access.
      </p>
      <div style={{ marginTop: '16px' }}>
        <Button asChild variant="secondary">
          <Link to="/admin/imports">Back to Imports</Link>
        </Button>
      </div>
    </section>
  );
}

function NotFoundPanel({
  id,
}: {
  readonly id: string | undefined;
}): React.ReactElement {
  return (
    <section
      aria-labelledby="import-not-found-heading"
      className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-16 text-center"
    >
      <h1
        id="import-not-found-heading"
        className="text-xl font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        Import not found
      </h1>
      <p className="mt-2 text-sm" style={{ color: 'var(--fg-secondary)' }}>
        {id === undefined
          ? 'No import id was provided.'
          : `No import found for id "${id}".`}
      </p>
      <div style={{ marginTop: '16px' }}>
        <Button asChild variant="secondary">
          <Link to="/admin/imports">Back to Imports</Link>
        </Button>
      </div>
    </section>
  );
}

function BackendNotWiredPanel({
  id,
}: {
  readonly id: string | undefined;
}): React.ReactElement {
  return (
    <section
      aria-labelledby="import-not-wired-heading"
      className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-16 text-center"
    >
      <h1
        id="import-not-wired-heading"
        className="text-xl font-semibold"
        style={{ color: 'var(--fg-primary)' }}
      >
        Import detail — coming soon
      </h1>
      <p className="mt-2 text-sm" style={{ color: 'var(--fg-secondary)' }}>
        The backend endpoint that powers this page hasn't shipped yet.
        Once <code>GET /admin/imports/:id</code> lands, this route will
        render the full import detail surface
        {id !== undefined ? <> for import <code>{id}</code></> : null}.
      </p>
      <div style={{ marginTop: '16px' }}>
        <Button asChild variant="secondary">
          <Link to="/admin/imports">Back to Imports</Link>
        </Button>
      </div>
    </section>
  );
}

function LoadingPanel(): React.ReactElement {
  return (
    <section
      aria-busy="true"
      aria-live="polite"
      className="mx-auto flex max-w-md flex-col items-center justify-center px-4 py-16 text-center"
      style={{ color: 'var(--fg-secondary)' }}
    >
      <p className="text-sm">Loading import detail…</p>
    </section>
  );
}

// ── Body ───────────────────────────────────────────────────────

function ImportDetailBody({
  importOverride,
}: ImportDetailPageProps): React.ReactElement {
  const { id } = useParams<{ id: string }>();
  const remote = useImportDetail(id);
  const result = React.useMemo<ImportDetailResult>(() => {
    if (importOverride === undefined) return remote;
    if (importOverride === null) {
      return { status: 'not_found' as const };
    }
    return { status: 'found' as const, importRecord: importOverride };
  }, [importOverride, remote]);

  if (result.status === 'not_wired') {
    return <BackendNotWiredPanel id={id} />;
  }
  if (result.status === 'loading') {
    return <LoadingPanel />;
  }
  if (result.status === 'not_found') {
    return <NotFoundPanel id={id} />;
  }

  const importRecord = result.importRecord;
  return <LoadedSurface importRecord={importRecord} />;
}

function LoadedSurface({
  importRecord,
}: {
  readonly importRecord: ImportDetail;
}): React.ReactElement {
  return (
    <article
      aria-labelledby="import-detail-heading"
      className="mx-auto w-full"
      style={{ maxWidth: '960px', padding: '32px' }}
    >
      <Header importRecord={importRecord} />
      <div
        className="flex flex-col"
        style={{ gap: '16px', marginTop: '20px' }}
      >
        <SourceFileSection importRecord={importRecord} />
        <ProgressSection importRecord={importRecord} />
        {importRecord.status === 'failed' ||
        importRecord.status === 'completed_with_errors' ? (
          <ErrorsSection importRecord={importRecord} />
        ) : null}
        <ActionsSection importRecord={importRecord} />
      </div>
    </article>
  );
}

// ── Header ─────────────────────────────────────────────────────

function Header({
  importRecord,
}: {
  readonly importRecord: ImportDetail;
}): React.ReactElement {
  return (
    <header
      data-testid="import-detail-header"
      className="flex flex-col"
      style={{ gap: '8px' }}
    >
      <nav
        aria-label="Breadcrumb"
        className="flex items-center"
        style={{ gap: '6px', fontSize: '12px', color: 'var(--fg-tertiary)' }}
      >
        <Link
          to="/admin/imports"
          style={{ color: 'var(--fg-secondary)' }}
        >
          ◀ Imports
        </Link>
        <span aria-hidden="true">/</span>
        <span
          style={{
            fontFamily: 'var(--font-mono, ui-monospace, monospace)',
            color: 'var(--fg-primary)',
          }}
        >
          {importRecord.id}
        </span>
      </nav>
      <h1
        id="import-detail-heading"
        className="m-0 font-bold"
        style={{
          fontSize: '22px',
          letterSpacing: '-0.4px',
          color: 'var(--fg-primary)',
        }}
      >
        {importTypeLabel(importRecord.type)} ·{' '}
        <Link
          to={`/admin/companies/${importRecord.company_id}`}
          style={{ color: 'var(--fg-primary)' }}
        >
          {importRecord.company_name}
        </Link>
      </h1>
      <div
        className="flex flex-wrap items-center"
        style={{ gap: '12px', fontSize: '13px', color: 'var(--fg-tertiary)' }}
      >
        <span className="flex items-center" style={{ gap: '6px' }}>
          <ImportStatusDot status={importRecord.status} />
          {STATUS_LABELS[importRecord.status]}
        </span>
        <span aria-hidden="true">·</span>
        <ImportTypeChip type={importRecord.type} size="sm" />
        <span aria-hidden="true">·</span>
        <span
          style={{
            fontFamily: 'var(--font-mono, ui-monospace, monospace)',
            fontSize: '12px',
          }}
        >
          {importRecord.initiator_email}
        </span>
        <span aria-hidden="true">·</span>
        <span>{formatRelativeTime(importRecord.created_at)}</span>
      </div>
    </header>
  );
}

// ── SectionCard ────────────────────────────────────────────────

function SectionCard({
  heading,
  headingId,
  action,
  variant,
  testId,
  children,
}: {
  readonly heading: string;
  readonly headingId: string;
  readonly action?: React.ReactNode;
  readonly variant?: 'danger';
  readonly testId?: string;
  readonly children: React.ReactNode;
}): React.ReactElement {
  const tone = variant === 'danger' ? 'var(--danger-500)' : 'var(--fg-primary)';
  return (
    <div data-testid={testId}>
      <Card padding="lg">
        <header
          className="flex items-center justify-between"
          style={{ gap: '12px', marginBottom: '12px' }}
        >
          <h2
            id={headingId}
            className="m-0 font-semibold"
            style={{ fontSize: '15px', color: tone }}
          >
            {heading}
          </h2>
          {action !== undefined && action !== null ? (
            <div>{action}</div>
          ) : null}
        </header>
        {children}
      </Card>
    </div>
  );
}

// ── Source file ────────────────────────────────────────────────

function SourceFileSection({
  importRecord,
}: {
  readonly importRecord: ImportDetail;
}): React.ReactElement {
  return (
    <SectionCard
      heading="Source file"
      headingId="import-detail-source"
      testId="import-detail-source"
    >
      <p
        className="m-0"
        style={{
          fontSize: '13px',
          color: 'var(--fg-primary)',
          marginBottom: '4px',
        }}
      >
        <strong>{importRecord.source_file_name}</strong> ·{' '}
        {formatFileSize(importRecord.source_file_size_bytes)}
      </p>
      <div style={{ marginTop: '12px' }}>
        <Button type="button" variant="secondary" size="sm">
          Download original
        </Button>
      </div>
    </SectionCard>
  );
}

// ── Progress ───────────────────────────────────────────────────

function ProgressSection({
  importRecord,
}: {
  readonly importRecord: ImportDetail;
}): React.ReactElement {
  return (
    <SectionCard
      heading="Progress"
      headingId="import-detail-progress"
      testId="import-detail-progress"
    >
      <p
        className="m-0"
        style={{
          fontSize: '13px',
          color: 'var(--fg-secondary)',
          marginBottom: '8px',
        }}
      >
        <span
          data-testid="import-detail-progress-counter"
          style={{ color: 'var(--fg-primary)' }}
        >
          {importRecord.rows_processed.toLocaleString()} /{' '}
          {importRecord.rows_total.toLocaleString()} rows
        </span>
      </p>
      <ProgressBar
        rowsProcessed={importRecord.rows_processed}
        rowsTotal={importRecord.rows_total}
      />
    </SectionCard>
  );
}

// ── Errors ─────────────────────────────────────────────────────

const ERRORS_INLINE_CAP = 50;

function ErrorsSection({
  importRecord,
}: {
  readonly importRecord: ImportDetail;
}): React.ReactElement {
  // Use `error_count` (the total errors reported by the BE) for
  // the header count, not the inline `errors[]` array length —
  // the BE may cap the inline list and the header should reflect
  // the real total either way.
  const totalErrors = importRecord.error_count;
  const visibleErrors = importRecord.errors.slice(0, ERRORS_INLINE_CAP);
  return (
    <SectionCard
      heading={`Errors (${totalErrors})`}
      headingId="import-detail-errors"
      testId="import-detail-errors"
      variant="danger"
      action={
        <Button type="button" variant="secondary" size="sm">
          Download error report CSV
        </Button>
      }
    >
      {totalErrors === 0 ? (
        <p
          className="m-0"
          style={{ fontSize: '13px', color: 'var(--fg-secondary)' }}
        >
          No row-level errors recorded.
        </p>
      ) : (
        <>
          <ul
            data-testid="import-detail-errors-list"
            className="m-0 flex list-none flex-col p-0"
            style={{ gap: '8px' }}
          >
            {visibleErrors.map((err, idx) => (
              <li
                // Spec §5: each error shows row number + column +
                // human message + raw value. Row numbers can
                // duplicate (same row, different column) so we
                // disambiguate with index.
                key={`${err.row_number}-${err.column}-${idx}`}
                style={{
                  padding: '10px 12px',
                  borderRadius: 'var(--radius-md)',
                  background: 'var(--bg-sunken)',
                  fontSize: '13px',
                }}
              >
                <div
                  className="font-medium"
                  style={{
                    color: 'var(--fg-primary)',
                    marginBottom: '2px',
                  }}
                >
                  Row {err.row_number} · column: {err.column}
                </div>
                <div style={{ color: 'var(--fg-secondary)' }}>
                  {err.message}
                </div>
                <div
                  style={{
                    marginTop: '4px',
                    fontFamily:
                      'var(--font-mono, ui-monospace, monospace)',
                    fontSize: '12px',
                    color: 'var(--fg-tertiary)',
                  }}
                >
                  {err.raw_value}
                </div>
              </li>
            ))}
          </ul>
          {visibleErrors.length < totalErrors ? (
            <p
              className="m-0"
              style={{
                fontSize: '12px',
                color: 'var(--fg-tertiary)',
                marginTop: '12px',
              }}
            >
              Showing first {visibleErrors.length} of {totalErrors}. Use
              the "Download error report CSV" button above for the
              full list.
            </p>
          ) : null}
        </>
      )}
    </SectionCard>
  );
}

// ── Actions ────────────────────────────────────────────────────

function ActionsSection({
  importRecord,
}: {
  readonly importRecord: ImportDetail;
}): React.ReactElement {
  const { can } = useAdminPermissions();
  const canRun = can('imports:run');
  return (
    <SectionCard
      heading="Actions"
      headingId="import-detail-actions"
      testId="import-detail-actions"
    >
      <PerStatusActions
        status={importRecord.status}
        canRun={canRun}
      />
    </SectionCard>
  );
}

function PerStatusActions({
  status,
  canRun,
}: {
  readonly status: ImportStatus;
  readonly canRun: boolean;
}): React.ReactElement {
  if (status === 'queued') {
    return (
      <div className="flex flex-wrap" style={{ gap: '8px' }}>
        {canRun ? (
          <Button type="button" variant="danger" size="sm">
            Cancel queue
          </Button>
        ) : (
          <p
            className="m-0"
            style={{ fontSize: '13px', color: 'var(--fg-tertiary)' }}
          >
            No actions available without the <code>imports:run</code>{' '}
            permission.
          </p>
        )}
      </div>
    );
  }
  if (status === 'running') {
    return (
      <p
        className="m-0"
        style={{ fontSize: '13px', color: 'var(--fg-secondary)' }}
      >
        No actions while the import is running. Phase 1 doesn't
        support force-cancel of in-flight imports — contact a
        platform administrator if a cancel is critical.
      </p>
    );
  }
  if (status === 'completed') {
    return (
      <div className="flex flex-wrap" style={{ gap: '8px' }}>
        {canRun ? (
          <Button type="button" variant="primary" size="sm">
            Re-run with updated file
          </Button>
        ) : (
          <p
            className="m-0"
            style={{ fontSize: '13px', color: 'var(--fg-tertiary)' }}
          >
            No actions available without the <code>imports:run</code>{' '}
            permission.
          </p>
        )}
      </div>
    );
  }
  if (status === 'completed_with_errors') {
    return (
      <div className="flex flex-wrap" style={{ gap: '8px' }}>
        {canRun ? (
          <Button type="button" variant="primary" size="sm">
            Re-run with corrected file
          </Button>
        ) : null}
        <Button type="button" variant="secondary" size="sm">
          Download error report
        </Button>
        {canRun ? (
          <Button type="button" variant="ghost" size="sm">
            Acknowledge errors
          </Button>
        ) : null}
      </div>
    );
  }
  // status === 'failed'
  return (
    <div className="flex flex-wrap" style={{ gap: '8px' }}>
      {canRun ? (
        <Button type="button" variant="primary" size="sm">
          Re-run with corrected file
        </Button>
      ) : null}
      <Button type="button" variant="secondary" size="sm">
        Download error report
      </Button>
    </div>
  );
}
